import { Dashboards } from '../dashboards-core';
import { DashboardWidgetHost } from './dashboard-widget-host';
import { findWidget, registeredWidgetKeys } from './dashboard-widget-registry';
import { DASHBOARD_COMPONENT_TYPES } from './dashboard-component-types';
import type { DashboardComponentKey } from './dashboard-component-types';
import { DASHBOARD_WIDGET_TYPE } from './dashboard-widget.contract';
import type { DashboardWidgetOptions } from './dashboard-widget.contract';

/** Namespaced so a widget key can never collide with a component type key. */
const WIDGET_ENTRY_PREFIX = 'widget:';

/** Sidebar entry key for one pool widget. */
export type WidgetEntryKey = `${typeof WIDGET_ENTRY_PREFIX}${string}`;

/** Everything the "Add Component" sidebar can offer. */
export type DashboardSidebarEntry = DashboardComponentKey | WidgetEntryKey;

/** Registers the per-widget sidebar */
declare module '@highcharts/dashboards/es-modules/Dashboards/Components/ComponentType' {
    interface ComponentTypeRegistry {
        [entry: WidgetEntryKey]: typeof DashboardWidgetHost;
    }
}

/** Widget keys that already have a sidebar entry in the global `Dashboards.ComponentRegistry`. */
const widgetKeysWithSidebarEntry = new Set<string>();

/** Registers a sidebar entry for every widget currently in the pool. Idempotent per key. */
export function registerWidgetSidebarEntries(): void {
    for (const widgetKey of registeredWidgetKeys()) {
        if (widgetKeysWithSidebarEntry.has(widgetKey)) {
            continue;
        }
        widgetKeysWithSidebarEntry.add(widgetKey);
        Dashboards.ComponentRegistry.registerComponent(
            entryKey(widgetKey),
            poolEntryType(widgetKey)
        );
    }
}

/** What the "Add Component" sidebar offers, and what each entry is called. `labelKeys` are i18n keys to resolve. */
export function authoringSidebar(): {
    components: DashboardSidebarEntry[];
    labelKeys: Record<string, string>;
    labels: Record<string, string>;
} {
    const components: DashboardSidebarEntry[] = [];
    const labelKeys: Record<string, string> = {};
    const labels: Record<string, string> = {};

    for (const type of DASHBOARD_COMPONENT_TYPES) {
        if (type.sidebarLabel !== undefined) {
            components.push(type.key);
            labelKeys[type.key] = type.sidebarLabel;
        }
    }

    for (const widgetKey of registeredWidgetKeys()) {
        const entry = entryKey(widgetKey);
        components.push(entry);
        const sidebarLabel = findWidget(widgetKey)?.sidebarLabel;
        if (sidebarLabel === undefined) {
            labels[entry] = widgetKey;
        } else {
            labelKeys[entry] = sidebarLabel;
        }
    }

    return { components, labelKeys, labels };
}

function entryKey(widgetKey: string): WidgetEntryKey {
    return `${WIDGET_ENTRY_PREFIX}${widgetKey}`;
}

/** A type that exists only to be dragged: dropping it places the widget it was minted for. */
function poolEntryType(widgetKey: string): typeof DashboardWidgetHost {
    return class WidgetPoolEntry extends DashboardWidgetHost {
        override getOptionsOnDrop(): Partial<DashboardWidgetOptions> {
            return { type: DASHBOARD_WIDGET_TYPE, widgetKey };
        }
    };
}
